import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/hooks/useSEO";
import { IMAGES, TEAM } from "@/data/brand";
import { Globe, Heart, Flame } from "lucide-react";

const CHAPTERS = [
  { n: "01", title: "It began with a house fire", body: "When Bobby was young, his family lived through a house fire that started at the clothes dryer — an electrical fire. Without any fire-safety education, his stepdad threw water on the flames, which is one of the most dangerous things you can do with an electrical fire. That frightening moment sparked Bobby's lifelong mission: no family should ever be caught unprepared." },
  { n: "02", title: "It led him to the front lines", body: "That experience drove Bobby to serve at a fire department for many years, where he led the fire prevention program — and where he first introduced himself and Pluggy to the community. Together they educated thousands of kids at daycares, libraries and school events, turning safety lessons into something kids couldn't wait to be part of." },
  { n: "03", title: "A prevention-first mission", body: "Bobby understood that the best way to fight fires is to prevent them. He wanted to put that same protective instinct into kids' hands — long before an emergency ever happens." },
  { n: "04", title: "A family-run adventure", body: "Together with Nekia Martin, Bobby turned safety education into a story worth telling: heroic characters, catchy songs, and lessons that kids beg to repeat." },
  { n: "05", title: "From Georgia to the world", body: "With international partner Kudakwashe Dzapasi, The Safety Quest is expanding across the US and Africa — one community, one classroom, one Safety Hero at a time." },
];

export default function About() {
  useSEO({ title: "About Us — Bobby, Nekia & the Team", description: "Meet Bobby Martin, former firefighter and founder of The Safety Quest, Nekia Martin, and international partner Kudakwashe Dzapasi." });

  return (
    <>
      <section className="pt-36 pb-20 max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-4">The Founder's Story</p>
          <h1 className="font-display text-6xl sm:text-8xl tracking-tight leading-[0.85] max-w-4xl">
            From firefighter to <span className="text-sq-red">safety storyteller</span>.
          </h1>
        </Reveal>
      </section>

      {/* Founder feature */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 grid lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="relative">
            <div className="absolute -inset-3 bg-sq-blue rounded-[2rem] -rotate-2" />
            <img src={IMAGES.bobby} alt="Bobby Martin, founder" className="relative rounded-[1.8rem] w-full bg-sq-bg object-contain aspect-[4/5] border-2 border-sq-ink" />
          </div>
        </Reveal>

        <div className="lg:col-span-7 space-y-10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="flex gap-6 border-b border-sq-ink/10 pb-8">
                <span className="font-display text-5xl text-sq-red/40 leading-none">{c.n}</span>
                <div>
                  <h3 className="font-round text-2xl font-semibold">{c.title}</h3>
                  <p className="text-sq-ink/70 mt-2 leading-relaxed">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-sq-ink text-white py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-6">
          {[
            { icon: Flame, t: "Prevention First", d: "Skills that stop emergencies before they start." },
            { icon: Heart, t: "Family-Run", d: "Built with heart by the Martin family." },
            { icon: Globe, t: "Global Vision", d: "Reaching communities across the US and Africa." },
          ].map((v, i) => (
            <Reveal key={v.t} delay={i * 0.1}>
              <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-8">
                <v.icon className="text-sq-yellow mb-4" size={30} />
                <h3 className="font-round text-2xl font-semibold">{v.t}</h3>
                <p className="text-white/60 mt-2">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-3">The Team</p>
          <h2 className="font-display text-5xl lg:text-7xl tracking-tight">Meet the heroes behind the quest</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.1}>
              <div data-testid={`team-${i}`} className="h-full rounded-3xl bg-white border-2 border-sq-ink p-8 shadow-[0_6px_0_#111827]">
                <div className="w-16 h-16 rounded-2xl bg-sq-yellow grid place-items-center font-display text-3xl text-sq-ink mb-5">
                  {m.name.split(" ").map((w) => w[0]).join("")}
                </div>
                <h3 className="font-round text-2xl font-semibold">{m.name}</h3>
                <p className="text-sq-red font-semibold text-sm mt-1">{m.role}</p>
                <p className="text-sq-ink/70 mt-4 leading-relaxed">{m.bio}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
